import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Typography,
  Box,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Email,
  Lock,
  Person,
  Business,
  ArrowForward,
  Google,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
// No longer using @react-oauth/google
import '../styles/auth.css';
import '../styles/auth-fix.css';
import '../styles/logo.css';
import logo from '../assets/logo.svg';

function Register() {
  const navigate = useNavigate();
  const { registerTenant, googleLogin, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    // Tenant data
    tenantName: '',
    // User data
    username: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // Check if already authenticated and redirect if needed
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else if (field === 'confirmPassword') {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const validateForm = () => {
    // Basic validation
    if (!formData.username || !formData.email || !formData.phoneNumber || !formData.password || !formData.confirmPassword || !formData.tenantName) {
      setError('Please fill in all required fields');
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Phone number validation
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/;
    if (!phoneRegex.test(formData.phoneNumber)) {
      setError('Please enter a valid phone number');
      return false;
    }

    // Password validation
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    // Password match validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    // Terms agreement validation
    if (!agreeToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Don't proceed if already loading
    if (isLoading) return;
    
    setError('');
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);

    try {
      // Prepare data for registration
      const tenantData = {
        name: formData.tenantName,
      };
      
      const userData = {
        username: formData.username,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
      };

      // Call the registerTenant function from AuthContext
      await registerTenant(tenantData, userData);
      
      // Navigation will be handled by the useEffect when isAuthenticated changes
    } catch (err) {
      console.error('Registration error:', err);
      
      // Handle specific error for duplicate email
      if (err.message && err.message.includes('already exists')) {
        setError('This email is already registered. Please use a different email or login.');
      } else {
        setError(err.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // Use Firebase for Google authentication
      const result = await googleLogin();
      console.log('Firebase login result:', result);
      
      // If this is a new Google user, they'll need to complete tenant setup
      if (result.needsTenantSetup) {
        navigate('/complete-registration', { 
          state: { googleUser: result.user } 
        });
      }
      // Otherwise, they're logged in and will be redirected by the useEffect
    } catch (err) {
      console.error('Firebase Google signup error:', err);
      setError(err.message || 'Google signup failed. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Left Panel */}
      <div className="auth-left-panel">
        <div className="auth-logo">
          <span style={{
            fontSize: '2.5rem',
            fontWeight: '900',
            background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 50%, #D4AF37 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '2px',
            textShadow: '0 2px 10px rgba(212, 175, 55, 0.3)'
          }}>Eazy Fatoora</span>
        </div>
        
        <div className="auth-left-content">
          <h1 className="auth-headline">Streamline your business operations</h1>
          <p className="auth-subheadline">One integrated platform to manage your entire business workflow.</p>
          
          <div className="auth-features">
            <div className="auth-feature">
              <h3 className="auth-feature-title">Inventory Management</h3>
              <p className="auth-feature-description">Track and manage your inventory with real-time updates</p>
            </div>
            
            <div className="auth-feature">
              <h3 className="auth-feature-title">Financial Reports</h3>
              <p className="auth-feature-description">Generate insightful reports for informed decision-making</p>
            </div>
            
            <div className="auth-feature">
              <h3 className="auth-feature-title">Customer Management</h3>
              <p className="auth-feature-description">Manage relationships and improve customer satisfaction</p>
            </div>
            
            <div className="auth-feature">
              <h3 className="auth-feature-title">Resource Planning</h3>
              <p className="auth-feature-description">Optimize resource allocation for maximum efficiency</p>
            </div>
          </div>
        </div>
        
        <div className="auth-footer">
          © {new Date().getFullYear()} Eazy Fatoora. All rights reserved.
        </div>
        
        <div className="animated-background">
          <div className="background-shape shape1"></div>
          <div className="background-shape shape2"></div>
          <div className="background-shape shape3"></div>
          <svg width="0" height="0">
            <filter id="noise">
              <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
              <feBlend mode="screen" />
            </filter>
          </svg>
        </div>
      </div>
      
      {/* Right Panel */}
      <div className="auth-right-panel">
        <div className="auth-form-container">
          <h2 className="auth-form-title">Create your account</h2>
          <p className="auth-form-subtitle">Start your 14-day free trial, no credit card required</p>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

        <form onSubmit={handleSubmit} className="auth-card">
          <div className="form-group">
            <label htmlFor="username">Full name</label>
            <div className="input-container">
              <Person className="input-icon" />
              <input
                type="text"
                id="username"
                name="username"
                placeholder="Your full name"
                value={formData.username}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Work email</label>
            <div className="input-container">
              <Email className="input-icon" />
              <input
                type="email"
                id="email"
                name="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="phoneNumber">Phone number</label>
            <div className="input-container">
              <Person className="input-icon" />
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                placeholder="+966 XX XXX XXXX"
                value={formData.phoneNumber}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="tenantName">Company name</label>
            <div className="input-container">
              <Business className="input-icon" />
              <input
                type="text"
                id="tenantName"
                name="tenantName"
                placeholder="Your company"
                value={formData.tenantName}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-container">
              <Lock className="input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                placeholder="Create a password"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <button 
                type="button" 
                className="password-toggle"
                onClick={() => togglePasswordVisibility('password')}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </button>
            </div>
            <small className="password-hint">Must be at least 8 characters</small>
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm password</label>
            <div className="input-container">
              <Lock className="input-icon" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
              <button 
                type="button" 
                className="password-toggle"
                onClick={() => togglePasswordVisibility('confirmPassword')}
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </button>
            </div>
          </div>
          
          <div className="terms-checkbox">
            <input 
              type="checkbox" 
              id="terms" 
              checked={agreeToTerms}
              onChange={() => setAgreeToTerms(!agreeToTerms)}
            />
            <label htmlFor="terms">
              I agree to the <Link to="/terms" className="terms-link">Terms of Service</Link> and <Link to="/privacy" className="terms-link">Privacy Policy</Link>
            </label>
          </div>
          
          <button 
            type="submit" 
            className="auth-button"
            disabled={isLoading || !agreeToTerms}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <>Create Account</>
            )}
          </button>
          
          <div className="divider">
            <span>Or continue with</span>
          </div>
          
          <div className="social-buttons">
            <button 
              type="button" 
              className="social-button google"
              onClick={handleGoogleSignup}
              disabled={isLoading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google
            </button>
            <button 
              type="button" 
              className="social-button microsoft"
              disabled={isLoading}
            >
              <svg width="20" height="20" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                <path fill="#f25022" d="M1 1h10v10H1z"/>
                <path fill="#00a4ef" d="M1 12h10v10H1z"/>
                <path fill="#7fba00" d="M12 1h10v10H12z"/>
                <path fill="#ffb900" d="M12 12h10v10H12z"/>
              </svg>
              Microsoft
            </button>
          </div>
          
          <div className="auth-footer-links">
            Already have an account? <Link to="/login">Sign in</Link>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

export default Register;